import {
  CaretDownIcon,
  CheckIcon,
  FloppyDiskIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { TextAreaField, TextField } from "../components/Field";
import { LoadingState } from "../components/LoadingState";
import type { SiteSetting } from "../types";

const SETTING_META: Record<
  string,
  { title: string; eyebrow: string; description: string }
> = {
  site: {
    title: "Сайт",
    eyebrow: "Бренд / SEO",
    description:
      "Название, описание, контакты, дефолтные изображения и SEO-суффикс.",
  },
  navigation: {
    title: "Навигация",
    eyebrow: "Header / меню",
    description: "Пункты меню, подписи, иконки и главная CTA-кнопка.",
  },
  social: {
    title: "Соцсети",
    eyebrow: "Контакты",
    description: "Ссылки на мессенджеры, почту и внешние профили.",
  },
  footer: {
    title: "Footer",
    eyebrow: "Подвал сайта",
    description: "Тексты футера, заголовки колонок и юридические ссылки.",
  },
  marketing: {
    title: "Маркетинг",
    eyebrow: "Analytics defaults",
    description: "Базовые настройки аналитики и захвата UTM-меток.",
  },
  forms: {
    title: "Формы",
    eyebrow: "Заявки",
    description: "Тексты формы, темы обращения и доступные форматы проекта.",
  },
};

const FIELD_LABELS: Record<string, string> = {
  analytics_provider: "Провайдер аналитики",
  contact_email: "Контактный email",
  contact_note: "Подпись к email",
  cta: "Главная кнопка",
  cta_label: "Текст кнопки",
  default_og_image: "Дефолтный OG image",
  description: "Описание",
  email: "Email",
  formats: "Форматы проекта",
  href: "Ссылка",
  icon: "Иконка",
  items: "Пункты",
  label: "Текст",
  legal_links: "Юридические ссылки",
  legal_title: "Заголовок правового блока",
  logo_image: "Логотип",
  measurement_id: "Measurement ID",
  modal_description: "Описание модалки",
  modal_title: "Заголовок модалки",
  name: "Название",
  phone: "Телефон",
  seo_title_suffix: "SEO-суффикс",
  social_title: "Заголовок соцсетей",
  studio_title: "Заголовок студии",
  sub: "Подпись",
  tagline: "Тэглайн",
  topics: "Темы обращения",
  twitter_handle: "Twitter / X",
  utm_capture: "Собирать UTM-метки",
  value: "Значение",
};

const LONG_TEXT_KEYS = [
  "description",
  "tagline",
  "message",
  "note",
  "body",
  "text",
];

export function SettingsPage() {
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Record<string, unknown>>>(
    {},
  );
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    const result = await apiRequest<SiteSetting[]>("/api/v1/admin/settings");
    setSettings(result);
    setDrafts(Object.fromEntries(result.map((item) => [item.key, item.value])));
    setLoading(false);
  }

  useEffect(() => {
    load().catch(() => {
      setError("Не удалось загрузить настройки");
      setLoading(false);
    });
  }, []);

  function toggleSetting(key: string) {
    setOpenKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function updateDraft(key: string, value: unknown) {
    if (!isRecord(value)) {
      return;
    }
    setDrafts((current) => ({ ...current, [key]: value }));
  }

  async function save(key: string) {
    setError("");
    setNotice("");
    setSavingKey(key);
    try {
      await apiRequest<SiteSetting>(`/api/v1/admin/settings/${key}`, {
        method: "PUT",
        body: JSON.stringify({ value: drafts[key] ?? {} }),
      });
      await load();
      setNotice("Настройки сохранены");
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось сохранить настройки",
      );
    } finally {
      setSavingKey("");
    }
  }

  if (loading) {
    return (
      <div className="page">
        <LoadingState rows={6} />
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Глобально</span>
          <h1>Настройки сайта</h1>
          <p>Контакты, соцсети, навигация, footer и marketing defaults.</p>
        </div>
      </header>

      {error ? <div className="notice notice--error">{error}</div> : null}
      {notice ? (
        <div className="notice notice--success">
          <CheckIcon size={17} /> {notice}
        </div>
      ) : null}

      <div className="settings-grid">
        {settings.map((item) => {
          const meta = getSettingMeta(item.key);
          const isOpen = openKeys.has(item.key);
          const draft = drafts[item.key] ?? item.value;
          const isDirty = JSON.stringify(draft) !== JSON.stringify(item.value);

          return (
            <section
              className={`settings-card ${isOpen ? "is-open" : "is-collapsed"}`}
              key={item.key}
            >
              <div className="settings-card__header">
                <button
                  aria-expanded={isOpen}
                  className="settings-card__toggle"
                  onClick={() => toggleSetting(item.key)}
                  type="button"
                >
                  <span className="settings-card__copy">
                    <span className="eyebrow">settings/{item.key}</span>
                    <strong>{meta.title}</strong>
                    <small>{meta.description}</small>
                  </span>
                  <span className="settings-card__summary">
                    {getSettingSummary(item.key, draft)}
                  </span>
                  <CaretDownIcon size={18} />
                </button>
                <Button
                  className="settings-card__save"
                  disabled={savingKey === item.key || !isDirty}
                  onClick={() => void save(item.key)}
                  tone="secondary"
                >
                  <FloppyDiskIcon size={16} />{" "}
                  {savingKey === item.key ? "Сохраняем" : "Сохранить"}
                </Button>
              </div>

              {isOpen ? (
                <div className="settings-card__body">
                  <SettingValueEditor
                    fieldKey={item.key}
                    onChange={(value) => updateDraft(item.key, value)}
                    value={draft}
                  />
                  <div className="settings-card__footer">
                    <span>{meta.eyebrow}</span>
                    <span>Обновлено: {formatDate(item.updated_at)}</span>
                  </div>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function SettingValueEditor({
  depth = 0,
  fieldKey,
  onChange,
  value,
}: {
  depth?: number;
  fieldKey: string;
  onChange: (value: unknown) => void;
  value: unknown;
}) {
  if (Array.isArray(value)) {
    return (
      <ArrayValueEditor
        depth={depth}
        fieldKey={fieldKey}
        onChange={onChange}
        value={value}
      />
    );
  }

  if (isRecord(value)) {
    const fields = Object.entries(value);
    const content = (
      <div
        className={
          depth === 0 ? "settings-fields" : "settings-fields is-nested"
        }
      >
        {fields.map(([key, nestedValue]) => (
          <SettingValueEditor
            depth={depth + 1}
            fieldKey={key}
            key={key}
            onChange={(nextValue) => onChange({ ...value, [key]: nextValue })}
            value={nestedValue}
          />
        ))}
      </div>
    );

    if (depth === 0) {
      return content;
    }

    return (
      <fieldset className="settings-fieldset">
        <legend>{humanizeKey(fieldKey)}</legend>
        {content}
      </fieldset>
    );
  }

  if (typeof value === "boolean") {
    return (
      <label className="settings-switch">
        <input
          checked={value}
          onChange={(event) => onChange(event.target.checked)}
          type="checkbox"
        />
        <span>
          <strong>{humanizeKey(fieldKey)}</strong>
          <small>{value ? "Включено" : "Выключено"}</small>
        </span>
      </label>
    );
  }

  if (typeof value === "number") {
    return (
      <TextField
        label={humanizeKey(fieldKey)}
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(nextValue === "" ? "" : Number(nextValue));
        }}
        type="number"
        value={value}
      />
    );
  }

  const stringValue = typeof value === "string" ? value : "";
  const needsTextarea =
    stringValue.length > 80 ||
    LONG_TEXT_KEYS.some((key) => fieldKey.toLowerCase().includes(key));

  if (needsTextarea) {
    return (
      <TextAreaField
        label={humanizeKey(fieldKey)}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        value={stringValue}
      />
    );
  }

  return (
    <TextField
      label={humanizeKey(fieldKey)}
      onChange={(event) => onChange(event.target.value)}
      value={stringValue}
    />
  );
}

function ArrayValueEditor({
  depth,
  fieldKey,
  onChange,
  value,
}: {
  depth: number;
  fieldKey: string;
  onChange: (value: unknown[]) => void;
  value: unknown[];
}) {
  function updateItem(index: number, nextValue: unknown) {
    onChange(
      value.map((item, itemIndex) => (itemIndex === index ? nextValue : item)),
    );
  }

  function addItem() {
    onChange([...value, createEmptyArrayItem(fieldKey, value)]);
  }

  function removeItem(index: number) {
    onChange(value.filter((_, itemIndex) => itemIndex !== index));
  }

  return (
    <fieldset className="settings-fieldset settings-fieldset--array">
      <legend>
        <span>{humanizeKey(fieldKey)}</span>
        <small>{value.length}</small>
      </legend>

      <div className="settings-array">
        {value.map((item, index) => (
          <div className="settings-array__item" key={`${fieldKey}-${index}`}>
            <div className="settings-array__head">
              <strong>
                {getArrayItemTitle(item) ||
                  `${humanizeKey(fieldKey)} ${index + 1}`}
              </strong>
              <button
                aria-label="Удалить"
                className="settings-icon-button settings-icon-button--danger"
                onClick={() => removeItem(index)}
                type="button"
              >
                <TrashIcon size={15} />
              </button>
            </div>
            <SettingValueEditor
              depth={depth + 1}
              fieldKey={`${fieldKey}_${index + 1}`}
              onChange={(nextValue) => updateItem(index, nextValue)}
              value={item}
            />
          </div>
        ))}
      </div>

      <button className="settings-add-button" onClick={addItem} type="button">
        <PlusIcon size={15} /> Добавить
      </button>
    </fieldset>
  );
}

function createEmptyArrayItem(fieldKey: string, items: unknown[]) {
  const firstObject = items.find(isRecord);
  if (firstObject) {
    return Object.fromEntries(
      Object.keys(firstObject).map((key) => [key, defaultValueForKey(key)]),
    );
  }

  if (fieldKey.includes("link")) {
    return { label: "", href: "" };
  }
  if (fieldKey === "topics" || fieldKey === "formats") {
    return { value: "", label: "" };
  }
  if (fieldKey === "items") {
    return { label: "", href: "", icon: "", sub: "" };
  }

  return "";
}

function defaultValueForKey(key: string) {
  if (key.startsWith("is_") || key.startsWith("has_")) {
    return false;
  }
  if (key.includes("order") || key.includes("count")) {
    return 0;
  }
  return "";
}

function getArrayItemTitle(item: unknown) {
  if (!isRecord(item)) {
    return typeof item === "string" ? item : "";
  }
  const label =
    item.label ?? item.title ?? item.name ?? item.href ?? item.value;
  return typeof label === "string" ? label : "";
}

function getSettingMeta(key: string) {
  return (
    SETTING_META[key] ?? {
      title: humanizeKey(key),
      eyebrow: "Глобальная настройка",
      description:
        "Аккуратное редактирование структуры без ручной правки JSON.",
    }
  );
}

function getSettingSummary(key: string, value: unknown) {
  if (!isRecord(value)) {
    return "настройка";
  }

  if (key === "site") {
    return [value.name, value.email].filter(Boolean).join(" · ") || "бренд";
  }
  if (key === "navigation" && Array.isArray(value.items)) {
    return `${value.items.length} пунктов`;
  }
  if (key === "social" && Array.isArray(value.items)) {
    return `${value.items.length} ссылок`;
  }
  if (key === "footer") {
    return typeof value.tagline === "string" ? value.tagline : "footer";
  }
  if (key === "marketing") {
    return value.utm_capture ? "UTM включены" : "UTM выключены";
  }
  if (key === "forms") {
    const topics = Array.isArray(value.topics) ? value.topics.length : 0;
    const formats = Array.isArray(value.formats) ? value.formats.length : 0;
    return `${topics} тем · ${formats} форматов`;
  }

  return `${Object.keys(value).length} полей`;
}

function humanizeKey(key: string) {
  return (
    FIELD_LABELS[key] ??
    key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(value));
}
